import type { InputHTMLAttributes } from 'react';

/**
 * The bounded-numeric-entry recipe — design-grammar §1 "Field measure" and
 * DECISIONS 2026-08-27 (grammar v3.3), which rules `<input type="number">` out
 * for bounded numeric entry.
 *
 * GOV.UK's Design System says: do not use `type="number"` unless research shows
 * a need. Their engineering write-up lists the failures, and every one of them
 * is silent — the user gets a wrong value with no feedback:
 *   - the scroll wheel changes a focused field when the page scrolls past it
 *   - `maxLength` does not apply, so length cannot be bounded
 *   - Chrome discards typed non-numeric characters without telling anyone
 *   - screen readers announce an unlabelled spin button
 *   - speech input (Dragon) cannot dictate into it
 *
 * The replacement is `type="text"` plus `inputMode` (which keypad), `pattern`
 * (which iOS keypad, pre-iOS-12.2) and `maxLength` (a real length bound).
 *
 * ONE CONSEQUENCE THE CALLER MUST HANDLE: `min`/`max`/`step` are inert on
 * anything but `type="number"` per the HTML spec, and a `text` box accepts any
 * character. So the caller sanitizes on change with `digitsOnly` and enforces
 * the RANGE itself. `maxLength` bounds digit COUNT, which is coarser than a
 * range — a 2-digit cap still admits 99 against a ceiling of 25.
 */

interface NumericInputOptions {
  /** The largest value the field accepts. The digit cap is derived from it, so
   *  the two cannot drift apart the way a hardcoded `maxLength` does. */
  max?: number;
  /** Money, rates and anything with a fractional part: a decimal keypad, and a
   *  pattern that admits one separator. An expense amount is not an integer. */
  decimal?: boolean;
  /** Override the derived digit cap — a decimal field needs room for the
   *  separator and its fraction, which the integer derivation does not know. */
  maxLength?: number;
  /** The field accepts negatives. A range bound in the form builder does: an
   *  income floor of −5,000 is a thing a staff author can legitimately set. */
  signed?: boolean;
}

type NumericInputProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'inputMode' | 'pattern' | 'maxLength'
>;

export function numericInputProps(
  { max, decimal, maxLength, signed }: NumericInputOptions = {},
): NumericInputProps {
  const derived = max !== undefined ? String(Math.floor(Math.abs(max))).length : undefined;
  const sign = signed ? '-?' : '';
  const room = (derived ?? 0) + (decimal ? 3 : 0) + (signed ? 1 : 0);
  return {
    type: 'text',
    // `numeric` is a digits-only keypad with no minus key, so a field that
    // accepts negatives has to ask for the fuller `decimal` pad instead.
    inputMode: decimal || signed ? 'decimal' : 'numeric',
    // `[0-9]*` is the documented iOS-keypad trigger; the decimal form has to
    // admit the separator or the pattern rejects every amount with cents.
    pattern: decimal ? `${sign}[0-9]*[.,]?[0-9]*` : `${sign}[0-9]*`,
    maxLength: maxLength ?? (derived !== undefined ? room : undefined),
  };
}

/**
 * Strip what a numeric box cannot mean, for the `onChange` that `type="text"`
 * now requires. Digits always; one separator in decimal mode, normalised to
 * `.` so `Number()` reads it; and a LEADING MINUS, always.
 *
 * The minus survives even on a field whose floor is zero, and that is the whole
 * point: silently turning "−50" into "50" is the failure this rule exists to
 * end — the user would submit a number they never typed and never be told. A
 * negative on a non-negative field is refused by the RANGE check, with the
 * bound stated, and `pattern` refuses it natively inside a validating form.
 * (This replaces an `onKeyDown` guard that swallowed the minus at the keypress,
 * which existed to dodge `type="number"`'s badInput state — a state a text box
 * does not have.)
 */
export function digitsOnly(raw: string, { decimal = false }: { decimal?: boolean } = {}): string {
  const prefix = raw.trimStart().startsWith('-') ? '-' : '';
  if (!decimal) return prefix + raw.replace(/\D/g, '');
  const cleaned = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const first = cleaned.indexOf('.');
  if (first === -1) return prefix + cleaned;
  // Keep the FIRST separator and drop the rest, so "12.3.4" becomes "12.34"
  // rather than being rejected wholesale while the user is still typing.
  return prefix + cleaned.slice(0, first + 1) + cleaned.slice(first + 1).replace(/\./g, '');
}
